// Question View - question screen layout with answers and radar chart
// Receives all data from ViewModel via props, no business logic

import { Dispatchers } from '@client/new/helpers/types';
import { PlaybookLogo } from '@design-system/components/logo/PlaybookLogo';
import { QuestionVM } from '../view-model/Question.viewModel';
import { RadarChartVM } from '../view-model/RadarChart.viewModel';
import {
  AnswerSummaryVM,
  OnboardingVMActionMap,
} from '../view-model/Onboarding.viewModel';
import { ProgressBar } from './components/ProgressBar';
import { AnswerOption } from './components/AnswerOption';
import { RadarChart } from './components/RadarChart';
import { AnswerChipList } from './components/AnswerChip';
import * as styles from './Question.view.css';

const STRATEGY_GUIDE_URL = 'https://www.joinplaybook.com/';

export type QuestionViewProps = {
  question: QuestionVM;
  currentStep: number;
  totalSteps: number;
  radarData: RadarChartVM;
  answeredSummary: AnswerSummaryVM[];
  canGoBack: boolean;
  isSaving: boolean;
} & Dispatchers<OnboardingVMActionMap>;

const BackButton = ({
  onClick,
  className,
}: {
  onClick: () => void;
  className: string;
}) => (
  <button
    type="button"
    className={className}
    onClick={onClick}
    data-testid="back-button"
    aria-label="Go back"
  >
    <span className={styles.backIcon}>←</span>
  </button>
);

const SkipButton = ({
  onClick,
  disabled,
  className,
}: {
  onClick: () => void;
  disabled: boolean;
  className: string;
}) => (
  <button
    type="button"
    className={className}
    onClick={onClick}
    disabled={disabled}
    data-testid="skip-button"
  >
    Skip for now
  </button>
);

const AnswerList = ({
  question,
  dispatch,
}: {
  question: QuestionVM;
  dispatch: Dispatchers<OnboardingVMActionMap>['dispatch'];
}) => (
  <>
    {question.answers.map((answer, index) => (
      <div
        key={answer.id}
        className={styles.answerItem}
        style={{ animationDelay: `${index * 0.05}s` }}
      >
        <AnswerOption
          id={answer.id}
          title={answer.title}
          subtitle={answer.subtitle}
          isSelected={answer.isSelected}
          onClick={() =>
            dispatch('SelectAnswer', {
              questionId: question.id,
              answerId: answer.id,
            })
          }
        />
      </div>
    ))}
  </>
);

export const QuestionView = ({
  question,
  currentStep,
  totalSteps,
  radarData,
  answeredSummary,
  canGoBack,
  isSaving,
  dispatch,
  lazyDispatch,
}: QuestionViewProps) => {
  const isFirstStep = currentStep === 1;

  if (isFirstStep) {
    return (
      <div className={styles.firstStepContainer} data-testid="question-view">
        <header className={styles.firstStepHeader}>
          {canGoBack && (
            <BackButton
              onClick={lazyDispatch('GoBack')}
              className={styles.backButtonFirstStep}
            />
          )}
          <PlaybookLogo showGlyph showName height={24} color="N-100" />
        </header>

        <div className={styles.firstStepCard}>
          <span className={styles.introBadge}>
            Create your thriving fitness app
          </span>

          <p className={styles.introDescription}>
            <strong>Get started quickly</strong> by answering just a few
            questions. We&apos;ll help tailor your first product to your needs,
            helping you to start making money as fast as possible.
          </p>

          <h1 className={styles.questionTitleFirstStep}>{question.title}</h1>

          <div className={styles.answersContainerFirstStep} key={question.id}>
            <AnswerList question={question} dispatch={dispatch} />
          </div>

          <SkipButton
            onClick={lazyDispatch('SkipToStrategy')}
            disabled={isSaving}
            className={styles.skipButtonFirstStep}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pageContainer} data-testid="question-view">
      <header className={styles.topHeader}>
        <div className={styles.logoSection}>
          <PlaybookLogo showGlyph showName height={24} color="N-100" />
        </div>
        <a
          href={STRATEGY_GUIDE_URL}
          className={styles.strategyLink}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="strategy-guide-link"
        >
          Get your own strategy guide
        </a>
      </header>

      <div className={styles.container}>
        <div className={styles.leftColumn}>
          {canGoBack && (
            <div className={styles.header}>
              <BackButton
                onClick={lazyDispatch('GoBack')}
                className={styles.backButton}
              />
            </div>
          )}

          <div className={styles.progressSection}>
            <ProgressBar current={currentStep} total={totalSteps} />
          </div>

          <div className={styles.questionSection}>
            <h1 className={styles.questionTitle}>{question.title}</h1>
            {question.subtitle && (
              <p className={styles.questionSubtitle}>{question.subtitle}</p>
            )}

            <div className={styles.answersContainer} key={question.id}>
              <AnswerList question={question} dispatch={dispatch} />
            </div>
          </div>
        </div>

        <div className={styles.rightColumn}>
          <div className={styles.radarSection}>
            <RadarChart vm={radarData} />
            <div className={styles.chipsSection}>
              <AnswerChipList summaries={answeredSummary} />
            </div>
          </div>
        </div>
      </div>

      <div className={styles.skipFooter}>
        <SkipButton
          onClick={lazyDispatch('SkipToStrategy')}
          disabled={isSaving}
          className={styles.skipButton}
        />
      </div>
    </div>
  );
};
